// src\components\ManagerPlanningView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Plus, Save, Filter, Home, Settings, AlertTriangle, Users, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import ManagerCalendarView from './planning/ManagerCalendarView';
import OrdersList from './planning/OrdersList';
import SelectedBatchDetail from './planning/SelectedBatchDetail';
import { useData } from '../contexts/DataContext';

const ManagerPlanningView = () => {
  const { 
    pendingOrders, 
    batchAssignments, 
    resources, 
    cycles,
    loading, 
    error, 
    assignOrderToBatch, 
    createBatch, 
    updateBatch 
  } = useData();

  // États locaux
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('fr-FR'));
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showNewBatchModal, setShowNewBatchModal] = useState(false);
  const [newBatch, setNewBatch] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDropAreaActive, setIsDropAreaActive] = useState(false);
  
  // Refs pour l'animation du glisser-déposer
  const orderListRef = useRef(null);
  const batchDetailRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Détecter la taille de l'écran pour l'affichage responsive
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Réinitialiser la fournée sélectionnée si elle n'existe plus dans les données
  useEffect(() => {
    if (selectedBatch && !batchAssignments.some(b => b.batchNumber === selectedBatch.batchNumber)) {
      setSelectedBatch(null);
    }
  }, [batchAssignments, selectedBatch]);

  // Fonction pour ajouter une nouvelle fournée
  const handleAddBatch = (resourceId, date, startTime, endTime) => {
    const resource = resources.find(r => r.id === resourceId);
    const resourceType = resource ? resource.type : null;
    
    // Filtrer les cycles compatibles avec cette ressource
    const compatibleCycles = cycles.filter(cycle => 
      cycle.compatibleWith.includes(resourceType)
    );
    
    // Définir les valeurs par défaut pour la nouvelle fournée
    setNewBatch({
      resourceId,
      date,
      startTime,
      endTime,
      compatibleCycles
    });
    
    setShowNewBatchModal(true);
  };

  // Fonction pour créer une nouvelle fournée
  const handleCreateBatch = async (cycleId) => {
    if (!newBatch) return;
    
    const cycle = cycles.find(c => c.id === cycleId);
    if (!cycle) return;
    
    // Créer l'objet de données pour la nouvelle fournée
    const batchData = {
      resourceId: newBatch.resourceId,
      date: newBatch.date,
      startTime: newBatch.startTime,
      endTime: newBatch.endTime,
      cycleName: cycle.name,
      code: cycle.code
    };
    
    // Appeler l'API pour créer la fournée
    const result = await createBatch(batchData);
    
    if (result.success) {
      // Fermer le modal
      setShowNewBatchModal(false);
      setNewBatch(null);
      
      // Sélectionner la nouvelle fournée
      setSelectedBatch(result.batch);
    } else {
      // Afficher l'erreur
      setAlertMessage(`Erreur lors de la création de la fournée: ${result.error}`);
      setTimeout(() => setAlertMessage(null), 5000);
    }
  };

  // Fonction pour gérer le drag & drop des ordres
  const handleOrderDrop = async (orderId, batchNumber) => {
    // Si on retire l'ordre d'une fournée (batchNumber est null)
    if (!batchNumber) {
      const result = await assignOrderToBatch(orderId, null);
      if (!result.success) {
        setAlertMessage(`Erreur lors du retrait de l'OF: ${result.error}`);
        setTimeout(() => setAlertMessage(null), 5000);
      }
      return;
    }
    
    // Trouver l'ordre à assigner et la fournée cible
    const orderToAssign = pendingOrders.find(order => order.id === orderId);
    const targetBatch = batchAssignments.find(batch => batch.batchNumber === batchNumber);
    
    if (!orderToAssign || !targetBatch) return;
    
    // Vérifier la compatibilité du cycle
    if (orderToAssign.cycleName !== targetBatch.cycleName) {
      setAlertMessage(`L'OF ${orderId} utilise le cycle ${orderToAssign.cycleName} et ne peut pas être assigné à une fournée ${targetBatch.cycleName}`);
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }
    
    // Trouver la ressource associée à la fournée
    const resource = resources.find(r => r.id === targetBatch.resourceId);
    
    // Vérifier si les contraintes sont respectées (prises de vide et thermocouples)
    const assignedOrders = pendingOrders.filter(order => order.assignedTo === batchNumber);
    const currentVacuumPorts = assignedOrders.reduce((sum, order) => sum + order.vacuumPorts, 0);
    const currentTc = assignedOrders.reduce((sum, order) => sum + order.tc, 0);
    
    const newVacuumPorts = currentVacuumPorts + orderToAssign.vacuumPorts;
    const newTc = currentTc + orderToAssign.tc;
    
    // Vérifier les limites de la ressource
    {/*if (resource && newVacuumPorts > resource.maxVacuumPorts) {
      setAlertMessage(`L'OF ${orderId} ne peut pas être assigné : limite de prises de vide dépassée (${newVacuumPorts}/${resource.maxVacuumPorts})`);
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }
    
    if (resource && newTc > resource.maxTc) {
      setAlertMessage(`L'OF ${orderId} ne peut pas être assigné : limite de thermocouples dépassée (${newTc}/${resource.maxTc})`);
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    } */}
    
    // Tout est ok, on assigne l'OF à la fournée
    const result = await assignOrderToBatch(orderId, batchNumber);
    
    if (!result.success) {
      setAlertMessage(`Erreur lors de l'assignation de l'OF: ${result.error}`);
      setTimeout(() => setAlertMessage(null), 5000);
    }
  };

  // Fonction pour déplacer des fournées
  const handleBatchMove = async (batchNumber, newResourceId, newDate, newStartTime, newEndTime) => {
    // Vérifier les conflits éventuels avec d'autres fournées
    const resource = resources.find(r => r.id === newResourceId);
    if (!resource || resource.status !== 'available') {
      setAlertMessage("Cette ressource n'est pas disponible pour planification");
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }
    
    // Tout est ok, on met à jour la fournée
    const batchData = {
      resourceId: newResourceId,
      date: newDate,
      startTime: newStartTime,
      endTime: newEndTime
    };
    
    const result = await updateBatch(batchNumber, batchData);
    
    if (!result.success) {
      setAlertMessage(`Erreur lors du déplacement de la fournée: ${result.error}`);
      setTimeout(() => setAlertMessage(null), 5000);
      return;
    }
    
    // Mettre à jour la fournée sélectionnée si nécessaire
    if (selectedBatch && selectedBatch.batchNumber === batchNumber) {
      const updatedBatch = batchAssignments.find(b => b.batchNumber === batchNumber);
      if (updatedBatch) {
        setSelectedBatch(updatedBatch);
      }
    }
  };

  // Fonction pour sélectionner une fournée
  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch);
  };

  // Gérer le début du glisser pour les interfaces tactiles
  const handleTouchStart = (e, orderId) => {
    // Stocker l'ID de l'ordre pour le traitement ultérieur si nécessaire
    e.currentTarget.setAttribute('data-order-id', orderId);
    
    // Montrer visuellement que l'élément est sélectionné
    e.currentTarget.classList.add('touch-dragging');
  };

  // Gérer le glissement pour les interfaces tactiles
  const handleTouchMove = (e) => {
    if (!e.currentTarget.classList.contains('touch-dragging')) return;
    
    const touch = e.touches[0];
    const orderElement = e.currentTarget;
    
    // Obtenir les coordonnées du doigt et de la zone de dépôt
    const touchX = touch.clientX;
    const touchY = touch.clientY;
    
    // Vérifier si le doigt est à proximité de la zone de dépôt
    if (batchDetailRef.current) {
      const batchRect = batchDetailRef.current.getBoundingClientRect();
      
      // Si le doigt se trouve à moins de 100px du bord de la zone de dépôt, l'activer
      const isNearDropZone = 
        touchX < batchRect.right + 100 && 
        touchY > batchRect.top && 
        touchY < batchRect.bottom;
      
      setIsDropAreaActive(isNearDropZone);
    }
  };

  // Gérer la fin du glissement pour les interfaces tactiles
  const handleTouchEnd = (e) => {
    const orderElement = e.currentTarget;
    const orderId = orderElement.getAttribute('data-order-id');
    orderElement.classList.remove('touch-dragging');
    
    // Vérifier si l'utilisateur a "jeté" l'OF vers la gauche (zone de détail de la fournée)
    if (isDropAreaActive && selectedBatch) {
      handleOrderDrop(orderId, selectedBatch.batchNumber);
    }
    
    // Réinitialiser l'état actif de la zone de dépôt
    setIsDropAreaActive(false);
  };

  // Obtenir les ordres assignés à la fournée sélectionnée
  const getAssignedOrders = () => {
    if (!selectedBatch) return [];
    return pendingOrders.filter(order => order.assignedTo === selectedBatch.batchNumber);
  };

  // Obtenir la ressource associée à la fournée sélectionnée
  const getSelectedBatchResource = () => {
    if (!selectedBatch) return null;
    return resources.find(r => r.id === selectedBatch.resourceId);
  };
  
  // Composant Modal pour la création d'une nouvelle fournée
  const NewBatchModal = () => {
    if (!newBatch || !showNewBatchModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-screen overflow-auto">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-bold">Nouvelle Fournée</h2>
          </div>
          
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ressource
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {resources.find(r => r.id === newBatch.resourceId)?.name}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                {newBatch.date}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure de début
                </label>
                <input 
                  type="time" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newBatch.startTime}
                  onChange={(e) => setNewBatch({...newBatch, startTime: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure de fin
                </label>
                <input 
                  type="time" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newBatch.endTime}
                  onChange={(e) => setNewBatch({...newBatch, endTime: e.target.value})}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cycle
              </label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {newBatch.compatibleCycles.map(cycle => (
                  <button
                    key={cycle.id}
                    className="px-4 py-2 border rounded-lg text-left hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => handleCreateBatch(cycle.id)}
                  >
                    <div className="font-medium">{cycle.name} - {cycle.code}</div>
                    <div className="text-sm text-gray-500">Durée: {cycle.duration}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t">
              <button 
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setShowNewBatchModal(false);
                  setNewBatch(null);
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Alerte pour les messages d'erreur ou de confirmation
  const AlertMessage = () => {
    if (!alertMessage) return null;
    
    return (
      <div className="fixed bottom-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-lg max-w-md animate-fade-in-out z-40">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="ml-3">
            <p className="text-sm">{alertMessage}</p>
          </div>
        </div>
      </div>
    );
  };

  // Afficher un chargement si les données sont en cours de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Afficher un message d'erreur si le chargement a échoué
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Affichage mobile
  if (isMobile) {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-blue-800 text-white py-2 px-4">
          <div className="flex justify-between items-center">
            <img src="/logo_bleu.png" alt="Logo" className="h-12 mr-2" />
            <h1 className="text-lg font-bold">Planification</h1>
            <div className="flex items-center space-x-2">
              <Link to="/" className="p-2 text-white hover:bg-blue-700 rounded-full">
                <Home size={20} />
              </Link>
              <Link to="/configuration" className="p-2 text-white hover:bg-blue-700 rounded-full">
                <Settings size={20} />
              </Link>
            </div>
          </div>
        </header>
        
        {/* Contenu principal avec onglets */}
        <div className="flex-grow flex flex-col overflow-hidden">
          {/* Sélecteur de Fournée */}
          <div className="p-2 bg-white border-b">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sélectionner une fournée</label>
            <select 
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={selectedBatch ? selectedBatch.batchNumber : ''}
              onChange={(e) => {
                const batchNumber = e.target.value;
                const batch = batchAssignments.find(b => b.batchNumber === batchNumber);
                setSelectedBatch(batch || null);
              }}
            >
              <option value="">-- Sélectionner une fournée --</option>
              {batchAssignments.map(batch => (
                <option key={batch.batchNumber} value={batch.batchNumber}>
                  {batch.batchNumber} - {batch.cycleName} ({batch.date})
                </option>
              ))}
            </select>
          </div>
          
          {/* Contenu des onglets */}
          <div className="flex-grow overflow-hidden flex flex-col">
            {selectedBatch ? (
              <div className="flex-grow flex flex-col">
                <div className="bg-blue-50 p-2 border-b">
                  <h2 className="font-bold text-blue-800">
                    Fournée {selectedBatch.batchNumber}
                  </h2>
                  <div className="flex justify-between text-sm">
                    <span>{resources.find(r => r.id === selectedBatch.resourceId)?.name}</span>
                    <span>{selectedBatch.cycleName} - {selectedBatch.startTime} - {selectedBatch.endTime}</span>
                  </div>
                </div>
                
                <div ref={batchDetailRef} className="p-2 flex-grow overflow-auto ">
                  <SelectedBatchDetail 
                    batch={selectedBatch}
                    resource={getSelectedBatchResource()}
                    assignedOrders={getAssignedOrders()}
                    onRemoveOrder={(orderId) => handleOrderDrop(orderId, null)}
                    pendingOrders={pendingOrders}
                  />
                </div>
                
                <div className="p-2 border-t">
                  <h3 className="font-medium text-gray-700 mb-2">OF disponibles pour cette fournée</h3>
                  <div ref={orderListRef} className="max-h-64 overflow-y-auto border rounded divide-y">
                    {pendingOrders
                      .filter(order => !order.assignedTo && order.cycleName === selectedBatch.cycleName)
                      .map(order => (
                        <div 
                          key={order.id}
                          className={`p-3 ${isDropAreaActive ? 'bg-blue-50' : ''} transition-all touch-action-pan-y`}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData('text/plain', order.id)}
                          onDragEnd={() => {}}
                          onTouchStart={(e) => handleTouchStart(e, order.id)}
                          onTouchMove={handleTouchMove}
                          onTouchEnd={handleTouchEnd}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium">{order.id}</div>
                              <div className="text-sm text-gray-600">{order.name}</div>
                            </div>
                            <button 
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full"
                              onClick={() => handleOrderDrop(order.id, selectedBatch.batchNumber)}
                            >
                              Ajouter
                            </button>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-grow flex items-center justify-center p-4">
                <div className="text-center text-gray-500">
                  <Calendar size={48} className="mx-auto text-gray-400 mb-2" />
                  <p>Veuillez sélectionner une fournée</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Navigation bottom */}
        <nav className="bg-white border-t py-2 px-4 flex justify-around">
          <Link to="/" className="flex flex-col items-center text-gray-600">
            <Home size={20} />
            <span className="text-xs mt-1">Production</span>
          </Link>
          <Link to="/manager" className="flex flex-col items-center text-blue-600">
            <Users size={20} />
            <span className="text-xs mt-1">Planning</span>
          </Link>
          <Link to="/configuration" className="flex flex-col items-center text-gray-600">
            <Settings size={20} />
            <span className="text-xs mt-1">Config</span>
          </Link>
          <Link to="/tv" className="flex flex-col items-center text-gray-600">
            <Monitor size={20} />
            <span className="text-xs mt-1">TV</span>
          </Link>
        </nav>
        
        {showNewBatchModal && <NewBatchModal />}
        <AlertMessage />
      </div>
    );
  }

  // Affichage tablette (layout adapté)
  if (isTablet) {
    return (
      <div className="flex flex-col h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-blue-800 text-white py-2">
          <div className="flex justify-between items-center px-4">
            <div className="flex items-center">
              <img src="/logo_bleu.png" alt="Logo" className="h-14 mr-3" />
              <h1 className="text-xl font-bold">Planification</h1>
            </div>
            <div className="flex items-center">
              <Link to="/" className="mr-2 p-2 text-white hover:bg-blue-700 rounded-full">
                <Home size={20} />
              </Link>
              <Link to="/configuration" className="mr-2 p-2 text-white hover:bg-blue-700 rounded-full">
                <Settings size={20} />
              </Link>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center">
                <Save size={18} className="mr-2" />
                Enregistrer
              </button>
            </div>
          </div>
        </header>
        
        {/* Main content - Layout adapté pour tablette */}
        <div className="flex-grow flex flex-col overflow-hidden">
          {/* Calendrier en haut */}
          <div className="h-1/2 p-2 overflow-auto">
            <ManagerCalendarView 
              resources={resources}
              batches={batchAssignments}
              onBatchSelect={handleBatchSelect}
              onBatchMove={handleBatchMove}
              onAddBatch={handleAddBatch}
              selectedBatch={selectedBatch}
            />
          </div>
          
          {/* Bas de page : détail de la fournée et liste des OF */}
          <div className="flex-grow flex overflow-hidden">
            {/* Détail de la fournée à gauche */}
            <div className="w-1/2 bg-white rounded-lg shadow m-2 overflow-hidden flex flex-col" ref={batchDetailRef}>
              <div className="p-3 border-b">
                <h2 className="font-semibold flex items-center">
                  {selectedBatch ? 
                    `Détail de la fournée ${selectedBatch.batchNumber}` : 
                    "Aucune fournée sélectionnée"}
                </h2>
              </div>
              
              <div className={`flex-grow overflow-y-auto ${isDropAreaActive ? 'bg-blue-50' : ''}`} ref={dropZoneRef}>
                {selectedBatch ? (
                  <SelectedBatchDetail 
                    batch={selectedBatch}
                    resource={getSelectedBatchResource()}
                    assignedOrders={getAssignedOrders()}
                    onRemoveOrder={(orderId) => handleOrderDrop(orderId, null)}
                    pendingOrders={pendingOrders}
                  />
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    Sélectionnez une fournée dans le calendrier pour voir ses détails et y affecter des OF
                  </div>
                )}
              </div>
            </div>
            
            {/* Liste des OF à droite */}
            <div className="w-1/2 bg-white rounded-lg shadow m-2 overflow-hidden flex flex-col" ref={orderListRef}>
              <div className="p-3 border-b">
                <h2 className="font-semibold flex items-center">
                  <Clock size={18} className="mr-2 text-blue-600" />
                  {selectedBatch ? 
                    `OF compatibles avec ${selectedBatch.cycleName}` : 
                    "Ordres de fabrication en attente"}
                </h2>
              </div>
              
              <div className="flex-grow overflow-auto">
                <OrdersList 
                  orders={pendingOrders}
                  selectedBatch={selectedBatch}
                  onOrderDrop={handleOrderDrop}
                  resources={resources}
                  batches={batchAssignments}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              </div>
            </div>
          </div>
        </div>
        
        {showNewBatchModal && <NewBatchModal />}
        <AlertMessage />
      </div>
    );
  }

  // Affichage desktop (layout original avec améliorations)
  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-blue-800 text-white py-2">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center">
              {/* Logo ajouté ici */}
              <img src="/logo_bleu.png" alt="Logo" className="h-14 mr-3" />
              <h1 className="text-xl font-bold">Planification des Fournées et affectation des OF</h1>
          </div>
          
          <div className="flex items-center mr-4">
            <Link to="/" className="mr-2 p-2 text-white hover:bg-blue-700 rounded-full" title="Vue Production">
              <Home size={20} />
            </Link>
            
            <Link to="/configuration" className="mr-2 p-2 text-white hover:bg-blue-700 rounded-full" title="Configuration">
              <Settings size={20} />
            </Link>

            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center">
              <Save size={18} className="mr-2" />
              Enregistrer
            </button>
          </div>
        </div>
      </header>
      
      {/* Main content - Layout modifié avec calendrier à gauche et contenu à droite */}
      <div className="flex-grow p-2 overflow-hidden flex px-3">
        {/* Calendrier à gauche - Largeur réduite */}
        <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col mr-2">
          <div className="p-2 border-b flex justify-between items-center">
            <div className="flex items-center">
              <Calendar size={18} className="mr-2 text-blue-600" />
              <h2 className="font-semibold">Calendrier des cycles</h2>
            </div>
            <button 
              className="px-3 py-1 bg-blue-600 text-white rounded-md flex items-center"
              onClick={() => {
                const availableResource = resources.find(r => r.status === 'available');
                if (availableResource) {
                  handleAddBatch(availableResource.id, selectedDate, '08:00', '12:00');
                }
              }}
            >
              <Plus size={16} className="mr-1" />
              Nouvelle fournée
            </button>
          </div>
          
          {/* Calendrier transposé */}
          <div className="flex-grow overflow-auto">
            <ManagerCalendarView 
              resources={resources}
              batches={batchAssignments}
              onBatchSelect={handleBatchSelect}
              onBatchMove={handleBatchMove}
              onAddBatch={handleAddBatch}
              selectedBatch={selectedBatch}
            />
          </div>
        </div>
        
        {/* Zone centrale pour détail fournée */}
        <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col flex-grow mr-2 p-2"
             ref={batchDetailRef}>
          {/* Détail de la fournée sélectionnée */}
          <div className="p-3 border-b">
            <h2 className="font-semibold flex items-center">
              {selectedBatch ? 
                `Détail de la fournée ${selectedBatch.batchNumber}` : 
                "Aucune fournée sélectionnée"}
            </h2>
          </div>
          
          <div className={`flex-grow overflow-y-auto ${isDropAreaActive ? 'bg-blue-50 transition-colors duration-200' : ''}`}
               ref={dropZoneRef}
               onDragOver={(e) => {
                 e.preventDefault();
                 if (selectedBatch) setIsDropAreaActive(true);
               }}
               onDragLeave={(e) => {
                 e.preventDefault();
                 setIsDropAreaActive(false);
               }}
               onDrop={(e) => {
                 e.preventDefault();
                 if (selectedBatch) {
                   const orderId = e.dataTransfer.getData('text/plain');
                   if (orderId) handleOrderDrop(orderId, selectedBatch.batchNumber);
                 }
                 setIsDropAreaActive(false);
               }}
          >
            {selectedBatch ? (
              <SelectedBatchDetail 
                batch={selectedBatch}
                resource={getSelectedBatchResource()}
                assignedOrders={getAssignedOrders()}
                onRemoveOrder={(orderId) => handleOrderDrop(orderId, null)}
                pendingOrders={pendingOrders}
              />
            ) : (
              <div className="p-8 text-center text-gray-500">
                Sélectionnez une fournée dans le calendrier pour voir ses détails et y affecter des OF
              </div>
            )}
          </div>
        </div> 
        
        {/* Liste des OF à droite */}
        <div className="px-1 bg-white rounded-lg shadow overflow-hidden flex flex-col flex-grow"
             ref={orderListRef}>
          <div className="p-3 border-b">
            <h2 className="font-semibold flex items-center">
              <Clock size={18} className="mr-2 text-blue-600" />
              {selectedBatch ? 
                `OF compatibles avec ${selectedBatch.cycleName}` : 
                "Ordres de fabrication en attente"}
            </h2>
          </div>
          
          <div className="flex-grow overflow-hidden">
            <OrdersList 
              orders={pendingOrders}
              selectedBatch={selectedBatch}
              onOrderDrop={handleOrderDrop}
              resources={resources}
              batches={batchAssignments}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          </div>
        </div>
      </div>
      
      {showNewBatchModal && <NewBatchModal />}
      <AlertMessage />
      
      <style jsx="true">{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(10px); }
        }
        
        .animate-fade-in-out {
          animation: fadeInOut 5s forwards;
        }
        
        .touch-dragging {
          opacity: 0.8;
          transform: scale(1.03);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          z-index: 10;
        }
        
        .touch-action-pan-y {
          touch-action: pan-y;
        }
      `}</style>
        <footer className="bg-gray-100 border-t py-2 px-4 text-center text-xs text-gray-500">
        © 2025 PolyPlan - Application de planification des moyens chauds | Éditeur : KaiDaTa
      </footer>
    </div>
  );
};

export default ManagerPlanningView;